import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import PremiumLicensesTemplate from 'components/templates/PremiumLicenses';
import Button from 'components/atoms/Button';
import { premiumLicenseService } from 'services/premiumLicenseService';
import { WaitingManager } from 'components/organisms/CommonUI/WaitingPopup/WaitingManager';
import { ToastManager } from 'components/organisms/CommonUI/ToastPopup/ToastManager';
import { ConfirmManager } from 'components/organisms/CommonUI/ConfirmPopup/ConfirmManager';
import { COLOR_SYSTEM } from 'components/design/design-system';
import { log, LogLevel } from 'utils/LogUtil';
import {
  exportAllPremiumLicenses,
  exportPremiumLicensesByRegion,
} from 'utils/exportPremiumLicenses';

const PremiumLicenses: React.FC = () => {
  // Form 상태
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [licenseNumbers, setLicenseNumbers] = useState<string>('');

  // List 상태
  const [licenses, setLicenses] = useState<{
    role: string;
    licenseNumbers: {
      license: string;
      isRegistered: boolean;
      hasValidRole?: boolean;
      createdAt?: string;
    }[];
    count: number;
  }[]>([]);

  // 로딩 상태
  const [isLoading, setIsLoading] = useState<boolean>(false);

  /**
   * Premium License 목록 조회
   */
  const loadLicenses = useCallback(async () => {
    try {
      setIsLoading(true);
      WaitingManager.show({
        message: 'Premium License 목록을 불러오는 중입니다...',
      });

      const response = await premiumLicenseService.getList({
        limit: 1000, // 충분히 큰 값으로 전체 조회
        offset: 0,
      });

      log(LogLevel.INFO, 'Premium License 목록 API 응답', response);

      // data 필드가 없거나 비어있는 경우 처리
      if (!response.data || response.data.length === 0) {
        setLicenses([]);
        log(LogLevel.INFO, 'Premium License 목록이 비어있습니다.');
        return;
      }

      // data를 권한별로 그룹화
      const groupedByRole: {
        [role: string]: {
          role: string;
          licenseNumbers: {
            license: string;
            isRegistered: boolean;
            hasValidRole?: boolean;
            createdAt?: string;
          }[];
          count: number;
        };
      } = {};

      response.data.forEach((item) => {
        if (!groupedByRole[item.roles]) {
          groupedByRole[item.roles] = {
            role: item.roles,
            licenseNumbers: [],
            count: 0,
          };
        }
        groupedByRole[item.roles].licenseNumbers.push({
          license: item.license,
          isRegistered: item.isRegistered || false,
          hasValidRole: item.hasValidRole,
          createdAt: item.createdAt,
        });
        groupedByRole[item.roles].count += 1;
      });

      const licenseList = Object.values(groupedByRole);
      setLicenses(licenseList);
      log(LogLevel.INFO, 'Premium License 목록 로드 완료', licenseList);
    } catch (error) {
      log(LogLevel.ERROR, 'Premium License 목록 로드 실패', error);
      ToastManager.show({
        content: 'Premium License 목록을 불러오는데 실패했습니다.',
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
      WaitingManager.hide();
    }
  }, []);

  // 컴포넌트 마운트 시 목록 로드
  useEffect(() => {
    loadLicenses();
  }, [loadLicenses]);

  /**
   * 면허번호 문자열을 배열로 파싱
   */
  const parseLicenseNumbers = (input: string): string[] => {
    return input
      .split('\n')
      .map((num) => num.trim().replace(/,/g, '')) // 쉼표 제거
      .filter((num) => num.length > 0);
  };

  /**
   * Premium License 추가
   */
  const handleAdd = useCallback(async () => {
    if (!selectedRole || !licenseNumbers.trim()) {
      ToastManager.show({
        content: '권한명과 면허번호를 모두 입력해주세요.',
        duration: 3000,
      });
      return;
    }

    const parsedLicenseNumbers = parseLicenseNumbers(licenseNumbers);
    if (parsedLicenseNumbers.length === 0) {
      ToastManager.show({
        content: '유효한 면허번호를 입력해주세요.',
        duration: 3000,
      });
      return;
    }

    ConfirmManager.show({
      title: 'Premium License 추가',
      body: `${selectedRole} 권한을 ${parsedLicenseNumbers.length}개의 면허번호에 추가하시겠습니까?`,
      buttons: [
        <Button
          key="cancel"
          buttonType="button"
          color="Secondary"
          size="Medium"
          type="Text"
          variant="Ghost"
          onClick={() => ConfirmManager.hide()}
        >
          취소
        </Button>,
        <Button
          key="confirm"
          buttonType="button"
          color="Primary"
          size="Medium"
          type="Text"
          variant="Contained"
          onClick={async () => {
            ConfirmManager.hide();
            await performAdd(parsedLicenseNumbers);
          }}
        >
          추가
        </Button>,
      ],
    });
  }, [selectedRole, licenseNumbers]);

  const performAdd = async (parsedLicenseNumbers: string[]) => {

    try {
      setIsLoading(true);
      WaitingManager.show({
        message: 'Premium License를 추가하는 중입니다...',
      });

      const result = await premiumLicenseService.bulkCreate({
        roles: selectedRole,
        licenses: parsedLicenseNumbers,
      });

      log(LogLevel.INFO, 'Premium License 추가 결과', result);

      // 결과 메시지 생성
      let message = '';
      if (result.inserted === 0) {
        message = `모든 면허번호가 이미 등록되어 있습니다. (건너뛴 개수: ${result.skipped}개)`;
      } else if (result.skipped > 0) {
        message = `${result.inserted}개의 면허번호가 등록되었습니다. (이미 존재: ${result.skipped}개)`;
      } else {
        message = `${result.inserted}개의 면허번호에 ${selectedRole} 권한이 추가되었습니다.`;
      }

      ToastManager.show({
        content: message,
        duration: 3000,
      });

      // 입력 초기화
      setLicenseNumbers('');

      // 목록 새로고침
      await loadLicenses();
    } catch (error) {
      log(LogLevel.ERROR, 'Premium License 추가 실패', error);
      ToastManager.show({
        content: 'Premium License 추가에 실패했습니다.',
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
      WaitingManager.hide();
    }
  };

  /**
   * Premium License 삭제 (Form에서 입력한 면허번호)
   */
  const handleDelete = useCallback(async () => {
    if (!selectedRole || !licenseNumbers.trim()) {
      ToastManager.show({
        content: '권한명과 면허번호를 모두 입력해주세요.',
        duration: 3000,
      });
      return;
    }

    const parsedLicenseNumbers = parseLicenseNumbers(licenseNumbers);
    if (parsedLicenseNumbers.length === 0) {
      ToastManager.show({
        content: '유효한 면허번호를 입력해주세요.',
        duration: 3000,
      });
      return;
    }

    ConfirmManager.show({
      title: 'Premium License 삭제',
      body: `${selectedRole} 권한을 ${parsedLicenseNumbers.length}개의 면허번호에서 삭제하시겠습니까?`,
      buttons: [
        <Button
          key="cancel"
          buttonType="button"
          color="Secondary"
          size="Medium"
          type="Text"
          variant="Ghost"
          onClick={() => ConfirmManager.hide()}
        >
          취소
        </Button>,
        <Button
          key="confirm"
          buttonType="button"
          color="Negative"
          size="Medium"
          type="Text"
          variant="Contained"
          onClick={async () => {
            ConfirmManager.hide();
            await performDelete(parsedLicenseNumbers);
          }}
        >
          삭제
        </Button>,
      ],
    });
  }, [selectedRole, licenseNumbers]);

  const performDelete = async (parsedLicenseNumbers: string[]) => {

    try {
      setIsLoading(true);
      WaitingManager.show({
        message: 'Premium License를 삭제하는 중입니다...',
      });

      await premiumLicenseService.bulkDelete({
        roles: selectedRole,
        licenses: parsedLicenseNumbers,
      });

      ToastManager.show({
        content: `${parsedLicenseNumbers.length}개의 면허번호에서 ${selectedRole} 권한이 삭제되었습니다.`,
        duration: 3000,
      });

      // 입력 초기화
      setLicenseNumbers('');

      // 목록 새로고침
      await loadLicenses();
    } catch (error) {
      log(LogLevel.ERROR, 'Premium License 삭제 실패', error);
      ToastManager.show({
        content: 'Premium License 삭제에 실패했습니다.',
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
      WaitingManager.hide();
    }
  };

  /**
   * 개별 면허번호 삭제
   */
  const handleDeleteLicense = useCallback(
    async (role: string, license: string) => {
      ConfirmManager.show({
        title: 'Premium License 삭제',
        body: `${role} 권한을 가진 면허번호 "${license}"를 삭제하시겠습니까?`,
        buttons: [
          <Button
            key="cancel"
            buttonType="button"
            color="Secondary"
            size="Medium"
            type="Text"
            variant="Ghost"
            onClick={() => ConfirmManager.hide()}
          >
            취소
          </Button>,
          <Button
            key="confirm"
            buttonType="button"
            color="Negative"
            size="Medium"
            type="Text"
            variant="Contained"
            onClick={async () => {
              ConfirmManager.hide();
              await performDeleteLicense(role, license);
            }}
          >
            삭제
          </Button>,
        ],
      });
    },
    []
  );

  const performDeleteLicense = async (role: string, license: string) => {
    try {
      setIsLoading(true);
      WaitingManager.show({
        message: 'Premium License를 삭제하는 중입니다...',
      });

      await premiumLicenseService.bulkDelete({
        roles: role,
        licenses: [license],
      });

      ToastManager.show({
        content: `${role} 권한에서 면허번호 "${license}"가 삭제되었습니다.`,
        duration: 3000,
      });

      // 목록 새로고침
      await loadLicenses();
    } catch (error) {
      log(LogLevel.ERROR, '면허번호 삭제 실패', error);
      ToastManager.show({
        content: '면허번호 삭제에 실패했습니다.',
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
      WaitingManager.hide();
    }
  };

  /**
   * 권한 부여 (더블클릭)
   */
  const handleGrantRole = useCallback(async (role: string, license: string) => {
    ConfirmManager.show({
      title: '권한 부여',
      body: `면허번호 "${license}"에 ${role} 권한을 부여하시겠습니까?`,
      buttons: [
        <Button
          key="cancel"
          buttonType="button"
          color="Secondary"
          size="Medium"
          type="Text"
          variant="Ghost"
          onClick={() => ConfirmManager.hide()}
        >
          취소
        </Button>,
        <Button
          key="confirm"
          buttonType="button"
          color="Primary"
          size="Medium"
          type="Text"
          variant="Contained"
          onClick={async () => {
            ConfirmManager.hide();
            await performGrantRole(role, license);
          }}
        >
          권한 부여
        </Button>,
      ],
    });
  }, []);

  const performGrantRole = async (role: string, license: string) => {
    try {
      setIsLoading(true);
      WaitingManager.show({
        message: '권한을 부여하는 중입니다...',
      });

      const result = await premiumLicenseService.addRoleByLicense({
        license,
        role,
      });

      log(LogLevel.INFO, '권한 부여 성공', result);

      ToastManager.show({
        content: result.message || `${role} 권한이 부여되었습니다.`,
        duration: 3000,
      });

      // 목록 새로고침
      await loadLicenses();
    } catch (error: any) {
      log(LogLevel.ERROR, '권한 부여 실패', error);

      // 404 에러인 경우 사용자가 없다는 메시지 표시
      if (error?.response?.status === 404) {
        ToastManager.show({
          content: error?.response?.data?.message || '해당 면허번호로 가입한 사용자를 찾을 수 없습니다.',
          duration: 3000,
        });
      } else {
        ToastManager.show({
          content: '권한 부여에 실패했습니다.',
          duration: 3000,
        });
      }
    } finally {
      setIsLoading(false);
      WaitingManager.hide();
    }
  };

  /**
   * 특정 권한의 모든 면허번호 삭제 (List에서)
   */
  const handleDeleteRole = useCallback(
    async (role: string, licenseNumbersToDelete: string[]) => {
      ConfirmManager.show({
        title: '권한 전체 삭제',
        body: `${role} 권한을 가진 ${licenseNumbersToDelete.length}개의 모든 면허번호를 삭제하시겠습니까?`,
        buttons: [
          <Button
            key="cancel"
            buttonType="button"
            color="Secondary"
            size="Medium"
            type="Text"
            variant="Ghost"
            onClick={() => ConfirmManager.hide()}
          >
            취소
          </Button>,
          <Button
            key="confirm"
            buttonType="button"
            color="Negative"
            size="Medium"
            type="Text"
            variant="Contained"
            onClick={async () => {
              ConfirmManager.hide();
              await performDeleteRole(role, licenseNumbersToDelete);
            }}
          >
            삭제
          </Button>,
        ],
      });
    },
    [loadLicenses]
  );

  const performDeleteRole = async (role: string, licenseNumbersToDelete: string[]) => {
    try {
      setIsLoading(true);
      WaitingManager.show({
        message: 'Premium License를 삭제하는 중입니다...',
      });

      await premiumLicenseService.bulkDelete({
        roles: role,
        licenses: licenseNumbersToDelete,
      });

      ToastManager.show({
        content: `${role} 권한이 모두 삭제되었습니다.`,
        duration: 3000,
      });

      // 목록 새로고침
      await loadLicenses();
    } catch (error) {
      log(LogLevel.ERROR, '권한 전체 삭제 실패', error);
      ToastManager.show({
        content: '권한 삭제에 실패했습니다.',
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
      WaitingManager.hide();
    }
  };

  /**
   * 전체 다운로드
   */
  const handleExportAll = useCallback(() => {
    exportAllPremiumLicenses(licenses);
  }, [licenses]);

  /**
   * 지역별 다운로드
   */
  const handleExportRegion = useCallback(
    (group: {
      role: string;
      licenseNumbers: { license: string; isRegistered: boolean; createdAt?: string }[];
    }) => {
      exportPremiumLicensesByRegion(group);
    },
    []
  );

  return (
    <PageContainer>
      <PremiumLicensesTemplate
        selectedRole={selectedRole}
        licenseNumbers={licenseNumbers}
        onRoleChange={setSelectedRole}
        onLicenseNumbersChange={setLicenseNumbers}
        onAdd={handleAdd}
        onDelete={handleDelete}
        licenses={licenses}
        onDeleteRole={handleDeleteRole}
        onDeleteLicense={handleDeleteLicense}
        onGrantRole={handleGrantRole}
        onExportAll={handleExportAll}
        onExportRegion={handleExportRegion}
        isLoading={isLoading}
      />
    </PageContainer>
  );
};

const PageContainer = styled.div`
  padding: 24px;
  min-height: 100vh;
  background-color: ${COLOR_SYSTEM.get('Gray')?.[50]};
`;

export default PremiumLicenses;
